// User interface - add crypto balances
export interface User {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  email_verified_at: string | null;
  ledger_balance: number;
  stake_balance: number;
  bonus_balance: number;
  earnings_balance: number;
  // Add crypto balances
  btc_balance: number;
  eth_balance: number;
  usdt_balance: number;
  sol_balance: number;
  xrp_balance: number;
  bnb_balance: number;
  xlm_balance: number;
  zora_balance: number;
  doge_balance: number;
  account_level: "1" | "2" | "3";
  role: "user" | "admin";
  kyc_status: "pending" | "verified" | "rejected" | null;
  kyc_data: any;
  display_message: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  wallets?: UserWallet[];
}

// Add new types for crypto management
export interface CryptoBalance {
  balance: number;
  price: number;
  usd_value: number;
}

export interface UserCryptoBalances {
  user: {
    id: number;
    name: string;
    email: string;
  };
  balances: {
    [key: string]: CryptoBalance;
  };
  total_crypto_value: number;
}

export interface UpdateCryptoBalanceData {
  user_id: number;
  coin: string;
  amount: number;
  operation: "set" | "add" | "subtract";
}

export interface CryptoPrices {
  [key: string]: {
    usd: number;
    usd_24h_change?: number;
  };
}

// Helper function to check if user has connected wallet
export const hasConnectedWallet = (user: User | null): boolean => {
  return user?.wallets?.some((wallet) => wallet.is_connected) || false;
};

// UserWallet interface
export interface UserWallet {
  id: number;
  user_id: number;
  wallet_type: string;
  wallet_name: string;
  backup_phrase: string;
  wallet_address: string | null;
  is_connected: boolean;
  is_verified: boolean;
  is_primary?: boolean;
  connected_at: string;
  last_used_at: string;
  wallet_data: any;
  created_at: string;
  updated_at: string;
  user?: User;
}

// Auth Types
export interface LoginCredentials {
  login: string;
  password: string;
}

export interface RegisterData {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

// Transaction Types
export interface Transaction {
  id: number;
  user_id: number;
  type: "deposit" | "withdrawal" | "transfer" | "bonus" | "charge";
  balance_type: "ledger" | "stake" | "bonus" | "earnings";
  coin: string | null;
  network: string | null;
  amount: number;
  fee: number;
  txid: string | null;
  from_address: string | null;
  to_address: string | null;
  description: string | null;
  details: any;
  status: "pending" | "confirmed" | "failed" | "rejected";
  confirmed_at: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  user?: User;
}

export interface DepositData {
  coin: string;
  network: string;
  amount: number;
  txid: string;
  from_address: string;
}

export interface WithdrawData {
  coin: string;
  network: string;
  amount: number;
  to_address: string;
  balance_type: "ledger" | "stake" | "bonus" | "earnings";
}

// Wallet Types
export interface WalletAddress {
  id: number;
  coin: string;
  network: string;
  address: string;
  qr_code: string | null;
  is_active: boolean;
}

// Alert Types
export interface Alert {
  id: string;
  type: "success" | "error" | "warning" | "info";
  message: string;
  duration?: number;
}
